/**
 * Protected Route Component
 *
 * Route guard that checks authentication before rendering protected content
 * Redirects to login if user is not authenticated
 */

import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/stores/auth.store';
import { PageLoader } from '@/components/common/PageLoader';

export interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute
 *
 * Wraps protected pages to ensure user is authenticated
 * - Checks auth status from Zustand store
 * - Validates tokens from localStorage
 * - Redirects to /login if not authenticated
 * - Preserves intended destination for redirect after login
 * - Shows loading state during authentication check
 *
 * @example
 * ```tsx
 * <ProtectedRoute>
 *   <DashboardPage />
 * </ProtectedRoute>
 * ```
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, checkAuth } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // Check authentication status on mount
    const performAuthCheck = () => {
      try {
        checkAuth();
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        setIsChecking(false);
      }
    };

    performAuthCheck();
  }, [checkAuth]);

  // Show loading state while checking authentication
  if (isChecking) {
    return <PageLoader />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the intended destination to redirect back after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // User is authenticated, render the protected content
  return <>{children}</>;
}
